import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { z } from "zod";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import FormTemplate from "../../templates/FormTemplate";


export type OwnerFormData = {
  name: string;
  phoneNumber: string;
  email: string;
  otherPhoneNumber: string;
};

type Props = {
  handleSubmit: (data: OwnerFormData, onError: (message: string) => void) => void;
  defaultValues?: Partial<OwnerFormData>;
  mode?: "create" | "edit";
};

export const OwnerForm: React.FC<Props> = ({
  handleSubmit,
  defaultValues = {},
  mode = "create"
}) => {
  const [hasNameError, setHasNameError] = useState<boolean>(false);
  const [hasPhoneNumberError, setHasPhoneNumberError] = useState<boolean>(false);
  const [hasEmailError, setHasEmailError] = useState<boolean>(false);
  const [hasOtherPhoneNumberError, setHasOtherPhoneNumberError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Local submission state to prevent multiple clicks
  const [isSubmitting, setIsSubmitting] = useState(false);



  const schema = useMemo(() => {
    return z.object({
      name: z.string().min(1, "Name is required"),
      phoneNumber: z.string().min(1, "Phone Number is required"),
      email: z.string().email("Invalid email address").optional().or(z.literal("")),
      otherPhoneNumber: z.string().optional(),
    });
  }, []);

  return (
    <FormTemplate
      schema={schema}
      setMethods={() => { }}
      isSubmitting={isSubmitting}
      defaultValues={{
        name: defaultValues.name || "",
        phoneNumber: defaultValues.phoneNumber || "",
        email: defaultValues.email || "",
        otherPhoneNumber: defaultValues.otherPhoneNumber || "",
      }}
      onSubmit={async (data) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);

        // Reset errors
        setHasNameError(false);
        setHasPhoneNumberError(false);
        setHasEmailError(false);
        setHasOtherPhoneNumberError(false);
        setErrorMessage(undefined);

        try {
          // Use a flag to track if onError was called
          let errorOccurred = false;

          // Wrap handleSubmit in a Promise
          await new Promise<void>((resolve, reject) => {
            handleSubmit(data as OwnerFormData, (message) => {
              errorOccurred = true;
              setErrorMessage(message);
              setIsSubmitting(false); // Reset submission state immediately on error
              reject(new Error(message));
            });

            // For successful submissions, resolve the promise after a short delay
            // Only resolve if no error occurred
            setTimeout(() => {
              if (!errorOccurred) {
                resolve();
              }
            }, 100);
          });
        } catch (error: any) {
          // Error is already handled by setting errorMessage and resetting isSubmitting above
          console.log('Owner form submission error handled:', error.message);
        }
        // Don't reset isSubmitting on success - let the modal close handle it
      }}
    >
      <StyledFormGrid>
        <StyledInputContainer>
          <Input
            label="Name"
            name="name"
            type="text"
            placeholder="Enter name"
            autoFocus
            required
            hasError={hasNameError}
            errorMessage={hasNameError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledInputContainer>
          <Input
            label="Phone Number"
            name="phoneNumber"
            type="number"
            placeholder="Enter phone number"
            required
            hasError={hasPhoneNumberError}
            errorMessage={hasPhoneNumberError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledInputContainer>
          <Input
            label="Email"
            name="email"
            type="email"
            placeholder="Enter email"
            hasError={hasEmailError}
            errorMessage={hasEmailError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledInputContainer>
          <Input
            label="Other Phone Number"
            name="otherPhoneNumber"
            type="number"
            placeholder="Enter other phone number"
            hasError={hasOtherPhoneNumberError}
            errorMessage={hasOtherPhoneNumberError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>
      </StyledFormGrid>

      <StyledFooter>
        <Button
          variant="filled"
          text={isSubmitting ? (mode === "create" ? "Creating..." : "Updating...") : (mode === "create" ? "Create" : "Update")}
          type="submit"
          isSubmitting={isSubmitting}
        />
      </StyledFooter>
    </FormTemplate>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;